import { useMemo } from "react";
import { api } from "../api";
import { CountryInput } from "../components/CountryInput";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { WorldMap } from "../components/WorldMap";
import { useGame } from "../useGame";

interface Guess {
  code: string;
  name: string;
  km: number;
}
interface View {
  guesses: Guess[];
  answer?: { code: string; name: string };
}

const COPY = { unknown_country: "Не знаю такої країни", already_guessed: "Вже було" };

/** Heat palette — mirrors heatEmoji buckets on the server. */
function heatColor(km: number): string {
  if (km === 0) return "#2e7d32";
  if (km < 400) return "#b71c1c";
  if (km < 800) return "#d84a3a";
  if (km < 1400) return "#e8703d";
  if (km < 2000) return "#ee9a3f";
  if (km < 3000) return "#f0c419";
  if (km < 4000) return "#d9d29a";
  return "#9fb2bf";
}

export function Hariache({ date }: { date?: string | undefined }) {
  const g = useGame<View>("hariache", COPY, date);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = useMemo(() => new Set(state?.view.guesses.map((x) => x.name) ?? []), [state?.view.guesses]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";

  const fills: Record<string, string> = {};
  for (const x of v.guesses) fills[x.code] = heatColor(x.km);
  if (v.answer) fills[v.answer.code] = heatColor(0);

  // Closest first — that's the trail you're following.
  const list = [...v.guesses].sort((a, b) => a.km - b.km);

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ГАРЯЧЕ-ХОЛОДНО" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <CountryInput
        disabled={finished}
        busy={g.busy}
        exclude={guessed}
        onSubmit={(name) => void g.run(() => api.guess<View>("hariache", name, date))}
      />
      <div className="px-4 py-2">
        <WorldMap tones={{}} fills={fills} height={210} />
        <div className="mt-1.5 text-center text-xs text-tghint">
          чим червоніша країна - тим ближче ціль · без напрямків, тільки жар
        </div>
      </div>

      <div className="flex-1 space-y-[6px] overflow-y-auto px-4 pb-2">
        {list.map((r) => (
          <div key={r.code} className="grid h-[40px] grid-cols-[16px_1fr_auto] items-center gap-2 rounded-lg bg-tgsection px-3 text-[15px]">
            <span className="h-3.5 w-3.5 rounded-full" style={{ background: heatColor(r.km) }} />
            <span className="truncate font-semibold">{r.name}</span>
            <span className="tabular-nums text-tghint">{r.km === 0 ? "🎉" : `${r.km.toLocaleString("en-US")} km`}</span>
          </div>
        ))}
      </div>

      {!finished && (
        <div className="flex gap-2 px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pt-1">
          <div className="flex flex-1 items-center text-xs text-tghint">спроб: {v.guesses.length}</div>
          <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("hariache", date))} />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Гаряче!" : "Здався"}
          subtitle={
            won
              ? `${v.guesses.length} спроб · стрік ${s.me.streak} 🔥`
              : `це був ${v.answer?.name ?? "?"} · стрік згорів`
          }
          scoreLabel="avg спроб"
          formatScore={(n) => `${n} спроб`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.answer && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <img src={`/flags/${v.answer.code.toLowerCase()}.svg`} alt="" className="h-8 rounded-sm" />
              <span className="text-lg font-semibold">{v.answer.name}</span>
            </div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
